import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { 
  User, 
  Bell, 
  Tag, 
  FormInput, 
  Shield, 
  Building2,
  ChevronRight,
  Users,
  Briefcase
} from 'lucide-react';

const settingsGroups = [
  {
    title: 'Personal',
    items: [
      {
        title: 'Account Settings',
        description: 'Manage your profile and change password',
        icon: User,
        href: '/settings/account',
        color: 'text-blue-500',
        bgColor: 'bg-blue-50'
      },
      {
        title: 'Notifications',
        description: 'Configure your notification preferences',
        icon: Bell,
        href: '/settings/notifications',
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-50'
      }
    ]
  },
  {
    title: 'Organization',
    items: [
      {
        title: 'Company Settings',
        description: 'Update company information and branding',
        icon: Building2,
        href: '/settings/company',
        color: 'text-teal-500',
        bgColor: 'bg-teal-50'
      },
      {
        title: 'Customer Settings',
        description: 'Manage industries and referral sources',
        icon: Users,
        href: '/settings/customers',
        color: 'text-green-500',
        bgColor: 'bg-green-50'
      }
    ]
  },
  {
    title: 'Data Management',
    items: [
      {
        title: 'Tags',
        description: 'Create and manage client tags',
        icon: Tag,
        href: '/settings/tags',
        color: 'text-purple-500',
        bgColor: 'bg-purple-50'
      },
      {
        title: 'Custom Fields',
        description: 'Add custom fields to client profiles',
        icon: FormInput,
        href: '/settings/custom-fields',
        color: 'text-indigo-500',
        bgColor: 'bg-indigo-50'
      }
    ]
  },
  {
    title: 'Security & Billing',
    items: [
      {
        title: 'Security',
        description: 'Manage security settings and two-factor authentication',
        icon: Shield,
        href: '/settings/security',
        color: 'text-red-500',
        bgColor: 'bg-red-50',
        disabled: true
      }
    ]
  }
];

const Settings = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your application settings and preferences</p>
      </div>

      {settingsGroups.map((group) => (
        <div key={group.title} className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-700">{group.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {group.items.map((item) => (
              <Card 
                key={item.title}
                className={`cursor-pointer transition-all hover:shadow-md hover:border-gray-300 ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !item.disabled && navigate(item.href)}
                data-testid={`settings-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${item.bgColor}`}>
                        <item.icon className={`h-5 w-5 ${item.color}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{item.title}</h3>
                        <p className="text-sm text-gray-500">{item.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                  {item.disabled && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full mt-3 inline-block">Coming soon</span>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Settings;
